import type { BeginSyncOptions, View } from "@novorender/api";
import { useCallback } from "react";

import { dataV2Headers } from "apis/dataV2/utils";
import { useAppStore } from "app/redux-store-interactions";
import { selectUser } from "slices/authSlice";
import { selectConfig } from "slices/explorer";
import { getAssetUrl } from "utils/misc";

import { captureHostData, createHostDataSources } from "./capture";

/**
 * The `captureHostData` callback for a full sync of the open scene, in Novorender's own
 * backend: see {@link captureHostData}. The personal bookmarks are the signed-in user's, as the
 * capture starts. `sceneIndexUrl` is the one the sync downloads from.
 */
export function useCaptureHostData() {
    const store = useAppStore();

    return useCallback(
        (projectId: string, view: View, sceneIndexUrl: URL): NonNullable<BeginSyncOptions["captureHostData"]> =>
            (writer, signal, version) =>
                captureHostData({
                    projectId,
                    user: selectUser(store.getState())?.user,
                    writer,
                    signal,
                    version,
                    sources: createHostDataSources({
                        projectId,
                        sceneIndexUrl,
                        dataV2: (path, init) => {
                            // Read per request, as the viewer's own requests are: a token
                            // refreshed during a long capture is picked up.
                            const state = store.getState();
                            const headers = dataV2Headers(state, new Headers(init.headers));
                            headers.set("accept", "application/json");
                            return fetch(selectConfig(state).dataV2ServerUrl + path, { ...init, headers });
                        },
                        committedDeviationsUrl: async () => (await getAssetUrl(view, "deviations.json")).toString(),
                    }),
                }),
        [store],
    );
}
